/**
 * Front desk: check in every visitor (scheduled, walk-in, with broker or C2C).
 * Check-in tells sales managers and admins (push + My Today "Waiting").
 *
 * The check-in form comes first, followed by visitors still at reception.
 * Front desk sees a compact lead match summary before check-in.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { useAuth } from '../AuthContext';
import {
  CAME_VIA_OPTIONS,
  VISIT_WITH_OPTIONS,
  buildPhoneSearchParts,
  buildTrigrams,
  dayKeyIST,
  leaveReceptionVisit,
  normalizeNameLower,
  receptionSummary,
  timeLabel,
  toDate,
  useReceptionToday,
} from '../utils/reception';
import type { CameVia, ReceptionDepartureReason, ReceptionVisit, VisitWith } from '../utils/reception';
import LeaveReceptionDialog from '../components/LeaveReceptionDialog';
import BrokerVisitPanel from '../components/BrokerVisitPanel';
import { formatFrontDeskName, joinBhaiSuffix } from '../utils/personName';

const DARK = '#1c1b19';
const GOLD = '#9d6f32';
const MUTED = '#5b5b5b';
const LINE = '#e8e3da';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const NEW_STATUS = 'T3lm25fKlGfyOCfLKeCK';

const mobileDigits = (input: string) => {
  const digits = input.replace(/\D/g, '');
  const withoutCountryCode = digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits;
  return withoutCountryCode.slice(0, 10);
};
const displayMobile = (digits: string) => digits.length > 5 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;
const cleanVisitorName = (value: string) => value.trim().replace(/\s+/g, ' ');
const validVisitorName = (value: string) => cleanVisitorName(value).length > 1 && !value.includes('--');

type LeadMatch = {
  id: string;
  name: string;
  ownerEmail: string | null;
  ownerName: string | null;
  projectId: string | null;
  projectName: string | null;
  bookedAt: Date | null;
  bookedProjectId: string | null;
  hasRefBy: boolean;
  status: string;
  visitCount: number;
  lastVisitAt: Date | null;
  checkedInToday: boolean;
};

type BrokerOption = { id: string; brokerName: string; agencyName: string | null };

const Pill = ({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    sx={{
      minHeight: 44, px: 2, borderRadius: 999, cursor: 'pointer', fontFamily: FONT, fontSize: 14,
      fontWeight: selected ? 700 : 500,
      border: selected ? 'none' : '1px solid #d9d3c8',
      bgcolor: selected ? GOLD : '#fff',
      color: selected ? '#fff' : '#1a1a1a',
    }}
  >
    {children}
  </Box>
);

const Label = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <Typography component={htmlFor ? 'label' : 'span'} {...(htmlFor ? { htmlFor } : {})}
    sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: MUTED, display: 'block', mb: 0.75 }}>
    {children}
  </Typography>
);

export default function FrontDeskPage() {
  const { currentUser, roleId } = useAuth();
  const standalone = String(roleId || '') === 'front-desk';
  const [now, setNow] = useState(() => new Date());
  const [visitMode, setVisitMode] = useState<'customer' | 'broker'>('customer');

  const [phone, setPhone] = useState('');
  const [matches, setMatches] = useState<LeadMatch[] | null>(null);
  const [lookupError, setLookupError] = useState(false);
  const [pickedLeadId, setPickedLeadId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [visitWith, setVisitWith] = useState<VisitWith | ''>('');
  const [visitWithOther, setVisitWithOther] = useState('');
  const [peopleCount, setPeopleCount] = useState(2);
  const [cameVia, setCameVia] = useState<CameVia | ''>('');
  const [broker, setBroker] = useState<BrokerOption | null>(null);
  const [brokerText, setBrokerText] = useState('');
  const [brokerSearchOpen, setBrokerSearchOpen] = useState(false);
  const [unlistedBrokerPhone, setUnlistedBrokerPhone] = useState('');
  const [c2cName, setC2cName] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [leaveFor, setLeaveFor] = useState<ReceptionVisit | null>(null);
  const [departureMessage, setDepartureMessage] = useState('');

  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [statusLabels, setStatusLabels] = useState<Map<string, string>>(new Map());
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const brokerMatches = useMemo(() => {
    const search = brokerText.trim().toLocaleLowerCase();
    if (search.length < 3) return [];
    return brokers.filter((item) =>
      item.brokerName.toLocaleLowerCase().includes(search) ||
      item.agencyName?.toLocaleLowerCase().includes(search)
    ).slice(0, 8);
  }, [brokers, brokerText]);

  // Owner names (leads often store only the owner's email).
  useEffect(
    () =>
      onSnapshot(collection(db, 'users'), (snap) => {
        const map = new Map<string, string>();
        snap.docs.forEach((d) => {
          const u = d.data() as any;
          const email = String(u.email || '').toLowerCase();
          if (email) map.set(email, String(u.displayName || u.name || u.email));
        });
        setUserNames(map);
      }, () => setUserNames(new Map())),
    []
  );
  useEffect(() => onSnapshot(collection(db, 'leadStatuses'), (snap) => {
    const labels = new Map<string, string>();
    snap.docs.forEach((item) => {
      const label = String(item.data().label || '').trim();
      if (label) labels.set(item.id, label);
    });
    setStatusLabels(labels);
  }, () => setStatusLabels(new Map())), []);
  const rows = useReceptionToday(db);

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(
    () =>
      onSnapshot(query(collection(db, 'projects'), orderBy('createdAt', 'asc')), (snap) =>
        setProjects(snap.docs.map((d) => ({ id: d.id, name: String(d.data().name || d.id) })))
      , () => setProjects([])),
    []
  );

  useEffect(
    () =>
      onSnapshot(collection(db, 'brokers'), (snap) =>
        setBrokers(
          snap.docs
            .map((d) => ({
              id: d.id,
              brokerName: String(d.data().brokerName || d.data().displayName || '').trim(),
              agencyName: String(d.data().agencyName || '').trim() || null,
            }))
            .filter((b) => b.brokerName)
            .sort((a, b) => a.brokerName.localeCompare(b.brokerName))
        )
      , () => setBrokers([])),
    []
  );


  const phone10 = phone;
  const validPhone = /^[6-9]\d{9}$/.test(phone10);
  const checkedInLeadIds = useMemo(
    () => new Set(rows.filter((r) => r.status === 'waiting' || r.status === 'with').map((r) => r.leadId)),
    [rows]
  );

  // Look up the number once 10 digits are in.
  useEffect(() => {
    setMatches(null);
    setLookupError(false);
    setPickedLeadId(null);
    setNewName('');
    if (!validPhone) return;
    let cancelled = false;
    (async () => {
      const leadsRef = collection(db, 'leads');
      const results = await Promise.allSettled([
        getDocs(query(leadsRef, where('phone10', '==', phone10), limit(5))),
        getDocs(query(leadsRef, where('phoneSearch', 'array-contains', phone10), limit(5))),
        getDocs(query(leadsRef, where('phone', 'in', [`+91${phone10}`, `91${phone10}`, phone10]), limit(5))),
      ]);
      if (cancelled) return;
      if (results.some((result) => result.status === 'rejected')) {
        setLookupError(true);
        return;
      }
      const leadDocs = new Map<string, QueryDocumentSnapshot>();
      results.forEach((result) => {
        if (result.status === 'fulfilled') result.value.docs.forEach((item) => leadDocs.set(item.id, item));
      });
      const today = dayKeyIST();
      const list: LeadMatch[] = Array.from(leadDocs.values()).map((d) => {
        const l = d.data() as Record<string, any>;
        const appt = l.appointment || {};
        const at = toDate(appt.scheduledAt);
        const bookedToday = !!at && dayKeyIST(at) === today && String(appt.status || 'scheduled') === 'scheduled';
        return {
          id: d.id,
          name: String(l.name || ''),
          ownerEmail: l.assignedTo || null,
          ownerName: l.assignedToName || userNames.get(String(l.assignedTo || '').toLowerCase()) || l.assignedTo || null,
          projectId: l.projectId || null,
          projectName: l.projectName || null,
          bookedAt: bookedToday ? at : null,
          bookedProjectId: bookedToday ? appt.projectId || null : null,
          hasRefBy: !!(l.referredBrokerId || l.referredBrokerName || l.refByName || l.refByType),
          status: String(l.status || ''),
          visitCount: Number(l.visitCount || 0),
          lastVisitAt: null,
          checkedInToday: false,
        };
      });
      setMatches(list);
      const first = list.find((m) => m.bookedAt) || list[0];
      if (first) pickLead(first);
      // Count actual logged visits and find the latest visit date. These details
      // do not block check-in if an older lead has incomplete activity history.
      const enriched = await Promise.all(list.map(async (match) => {
        try {
          const visits = await getDocs(query(collection(db, 'leads', match.id, 'activities'), where('type', '==', 'visit')));
          const dates = visits.docs.map((item) => toDate(item.data().at)).filter((date): date is Date => !!date);
          return {
            ...match,
            visitCount: visits.size || match.visitCount,
            lastVisitAt: dates.length ? new Date(Math.max(...dates.map((date) => date.getTime()))) : null,
          };
        } catch {
          return match;
        }
      }));
      if (!cancelled) setMatches(enriched);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone10, validPhone]);

  const pickLead = (m: LeadMatch) => {
    setPickedLeadId(m.id);
    setNewName(joinBhaiSuffix(m.name));
    setProjectId(m.bookedProjectId || m.projectId || '');
    setCameVia('');
    setBroker(null);
    setBrokerText('');
    setBrokerSearchOpen(false);
    setUnlistedBrokerPhone('');
    setC2cName('');
  };

  const picked = matches?.find((m) => m.id === pickedLeadId) || null;
  const isNew = !!matches && matches.length === 0;
  const needsFirstRef = isNew || (!!picked && picked.visitCount === 0 && !picked.hasRefBy);
  const alreadyIn = !!picked && checkedInLeadIds.has(picked.id);
  // Visited earlier today (done or left): a second check-in must be on purpose.
  const earlierToday = picked && !alreadyIn
    ? [...rows].reverse().find((r) => r.leadId === picked.id && (r.status === 'done' || r.status === 'left')) || null
    : null;
  const [confirmRepeat, setConfirmRepeat] = useState(false);
  useEffect(() => setConfirmRepeat(false), [pickedLeadId]);

  const reset = () => {
    setPhone('');
    setMatches(null);
    setLookupError(false);
    setPickedLeadId(null);
    setNewName('');
    setProjectId('');
    setVisitWith('');
    setVisitWithOther('');
    setPeopleCount(2);
    setCameVia('');
    setBroker(null);
    setBrokerText('');
    setBrokerSearchOpen(false);
    setUnlistedBrokerPhone('');
    setC2cName('');
  };

  const canCheckIn =
    validPhone && !!matches && !alreadyIn && (!earlierToday || confirmRepeat) && projects.some((p) => p.id === projectId) && !!visitWith && !!cameVia &&
    (isNew || !!picked) && validVisitorName(newName) &&
    (cameVia !== 'broker' || !!broker || brokerText.trim().length > 1) &&
    (cameVia !== 'broker' || !!broker || /^[6-9]\d{9}$/.test(unlistedBrokerPhone)) &&
    (cameVia !== 'c2c' || c2cName.trim().length > 1);

  const checkIn = async () => {
    if (!canCheckIn || !currentUser) return;
    setSaving(true);
    setMessage(null);
    try {
      const by = currentUser.email || null;
      const project = projects.find((p) => p.id === projectId) || null;
      const resolvedBroker = cameVia === 'broker' && !broker
        ? (await httpsCallable(functions, 'brokerCustomerVisitResolve')({
          phone: unlistedBrokerPhone, name: formatFrontDeskName(brokerText),
        })).data as {brokerId: string; name: string; agencyName: string | null; phone10: string}
        : null;
      const selectedBrokerId = broker?.id || resolvedBroker?.brokerId || null;
      const selectedAgency = broker?.agencyName || resolvedBroker?.agencyName || null;
      const batch = writeBatch(db);
      const leadRef = picked ? doc(db, 'leads', picked.id) : doc(collection(db, 'leads'));
      const name = picked ? joinBhaiSuffix(cleanVisitorName(newName)) : formatFrontDeskName(newName);
      const brokerName = cameVia === 'broker' ? broker?.brokerName || resolvedBroker?.name || formatFrontDeskName(brokerText) : null;
      const refMode = cameVia === 'broker' ? 'broker' : cameVia === 'c2c' ? 'c2c' : 'direct';
      const refFields = needsFirstRef ? {
        referredBrokerId: refMode === 'broker' ? selectedBrokerId : null,
        referredBrokerName: refMode === 'broker' ? brokerName : null,
        referredAgencyName: refMode === 'broker' ? selectedAgency : null,
        referredBrokerAt: refMode === 'broker' ? serverTimestamp() : null,
        refByName: refMode === 'c2c' ? c2cName.trim() : null,
        refByType: refMode,
        refByAt: serverTimestamp(),
      } : {};
      if (!picked) {
        const source = cameVia === 'broker' ? 'With Broker' : cameVia === 'c2c' ? 'C2C' : 'Walk In';
        batch.set(leadRef, {
          name,
          nameLower: normalizeNameLower(name),
          nameTrigrams: buildTrigrams(name),
          projectId: project?.id || null,
          projectName: project?.name || null,
          unitType: null,
          phone: `+91${phone10}`,
          phone10,
          phoneSearch: buildPhoneSearchParts(phone10),
          email: null,
          emailLower: null,
          source,
          sourceLower: source.toLowerCase(),
          ...refFields,
          status: NEW_STATUS,
          // Unassigned: the sales manager who presents gets it.
          assignedTo: null,
          assignedAt: null,
          assignedToName: null,
          nextFollowUpDate: null,
          nextFollowUpAt: null,
          notes: [],
          activityCount: 0,
          visitCount: 0,
          lastActivityAt: null,
          promptAnswers: {},
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: currentUser.uid,
        });
      }
      const visitRef = doc(collection(db, 'receptionVisits'));
      const visit = {
        dayKey: dayKeyIST(),
        status: 'waiting',
        leadId: leadRef.id,
        name,
        phone10,
        isNewLead: !picked,
        projectId: project?.id || null,
        projectName: project?.name || null,
        visitWith: visitWith || null,
        visitWithOther: visitWith === 'other' ? visitWithOther.trim() || null : null,
        peopleCount: visitWith === 'alone' ? 1 : peopleCount,
        cameVia: cameVia || null,
        brokerId: cameVia === 'broker' ? selectedBrokerId : null,
        brokerPresent: cameVia === 'broker',
        brokerName,
        brokerPhone10: cameVia === 'broker' && !broker ? unlistedBrokerPhone || null : null,
        agencyName: cameVia === 'broker' ? selectedAgency : null,
        c2cName: cameVia === 'c2c' ? c2cName.trim() : null,
        bookedAt: picked?.bookedAt ? Timestamp.fromDate(picked.bookedAt) : null,
        ownerEmail: picked?.ownerEmail || null,
        ownerName: picked?.ownerName || null,
        checkedInAt: serverTimestamp(),
        checkedInBy: by,
        takenAt: null,
        presenterEmail: null,
        presenterName: null,
        finishedAt: null,
        leftAt: null,
      };
      batch.set(visitRef, visit);
      batch.update(leadRef, {
        receptionVisit: { ...visit, id: visitRef.id },
        lastActivityAt: serverTimestamp(),
        ...(picked ? refFields : {}),
        ...(picked && name !== picked.name ? {
          name,
          nameLower: normalizeNameLower(name),
          nameTrigrams: buildTrigrams(name),
          updatedAt: serverTimestamp(),
        } : {}),
      });
      batch.set(doc(collection(leadRef, 'activities')), {
        type: 'internal',
        event: 'reception-check-in',
        notes: [`Checked in at reception: ${[project?.name, brokerName ? `via Broker ${brokerName}` : cameVia].filter(Boolean).join(' · ')}.`],
        byEmail: by,
        at: serverTimestamp(),
      });
      await batch.commit();
      setMessage({ kind: 'ok', text: `${name} checked in. Sales managers have been told.` });
      reset();
    } catch (err: any) {
      setMessage({ kind: 'error', text: `Could not check in: ${err?.message || err}` });
    } finally {
      setSaving(false);
    }
  };

  const form = (
    <Box component="section" aria-label="Visitor in"
      sx={{ bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 4, p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {standalone && <Typography component="h1" sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 700 }}>Visitor in</Typography>}

      <Box>
        <Label htmlFor="fd-phone">Mobile number</Label>
        <Box component="input" id="fd-phone" type="tel" inputMode="numeric" autoComplete="tel-national"
          value={displayMobile(phone)} aria-invalid={phone.length === 10 && !validPhone}
          aria-describedby={phone.length === 10 && !validPhone ? 'fd-phone-help' : undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const next = mobileDigits(e.target.value);
            e.target.value = displayMobile(next);
            setPhone(next);
          }}
          placeholder="12345 67890"
          sx={{ width: '100%', boxSizing: 'border-box', height: 54, fontSize: 20, fontFamily: FONT, px: 2,
            border: `2px solid ${phone.length === 10 && !validPhone ? '#b3261e' : GOLD}`, borderRadius: 3, letterSpacing: 1, outline: 'none' }} />
        {phone.length === 10 && !validPhone && <Typography id="fd-phone-help" role="alert"
          sx={{ fontFamily: FONT, fontSize: 12.5, mt: 0.75, color: '#b3261e' }}>
          Enter a mobile number starting with 6, 7, 8 or 9.
        </Typography>}
      </Box>

      {matches && matches.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {matches.map((m) => {
            const sel = m.id === pickedLeadId;
            const statusLabel = statusLabels.get(m.status) || (m.status && !/^[A-Za-z0-9_-]{16,}$/.test(m.status) ? m.status : 'Status unavailable');
            const lastVisitLabel = m.lastVisitAt
              ? new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }).format(m.lastVisitAt)
              : '—';
            return (
              <Box key={m.id} component="button" type="button" onClick={() => pickLead(m)}
                aria-pressed={sel}
                sx={{ width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: FONT, border: `1px solid ${sel ? '#b88746' : LINE}`,
                  borderLeft: `4px solid ${sel ? GOLD : '#ded8cf'}`, bgcolor: sel ? '#fffcf7' : '#fff', borderRadius: 2.5,
                  px: 2, py: 1.75, transition: 'background-color 180ms ease, border-color 180ms ease',
                  '&:hover': { bgcolor: sel ? '#fff9ed' : '#faf8f4', borderColor: '#b88746' },
                  '&:focus-visible': { outline: `3px solid ${GOLD}`, outlineOffset: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: DARK, flex: 1, minWidth: 0 }}>{m.name}</Typography>
                  {sel && <Box component="span" sx={{ px: 1, py: 0.4, borderRadius: 1.25, bgcolor: '#e7f3e8', color: '#226231', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>Selected</Box>}
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: m.bookedAt ? '#226231' : MUTED, fontWeight: m.bookedAt ? 700 : 500 }}>
                  {m.bookedAt ? `Scheduled today at ${timeLabel(m.bookedAt)}` : 'No visit scheduled today'}
                  {m.projectName ? ` · ${m.projectName}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                  <Box component="span" sx={{ px: 1.1, py: 0.55, borderRadius: 1.25, bgcolor: '#f2eee7', color: '#493b2c', fontSize: 12.5, fontWeight: 700 }}>
                    {statusLabel}
                  </Box>
                  <Box component="span" sx={{ px: 1.1, py: 0.55, borderRadius: 1.25, bgcolor: '#f4f4f3', color: '#353535', fontSize: 12.5, fontWeight: 700 }}>
                    {m.visitCount} completed visit{m.visitCount === 1 ? '' : 's'}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25, mt: 1.25, pt: 1, borderTop: '1px solid #ebe6df' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}><b>Last visit:</b> {lastVisitLabel}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}><b>Assigned to:</b> {m.ownerName || 'Unassigned'}</Typography>
                </Box>
              </Box>
            );
          })}
          {alreadyIn && (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#a35f00', fontWeight: 600 }}>Already checked in and still here.</Typography>
          )}
          {earlierToday && (
            <Box sx={{ border: '1px solid #f0c98f', bgcolor: '#fff8ec', borderRadius: 3, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: '#8a5a12' }}>
                Already visited today at {timeLabel(earlierToday.checkedInAt)}
                {earlierToday.status === 'done' && earlierToday.presenterName ? `, with ${earlierToday.presenterName}` : ''}
                {earlierToday.status === 'left' ? ' (left without meeting)' : ''}.
              </Typography>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: FONT, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={confirmRepeat} onChange={(e) => setConfirmRepeat(e.target.checked)} style={{ width: 20, height: 20 }} />
                They came back. Check in again.
              </Box>
            </Box>
          )}
        </Box>
      )}

      {lookupError && <Typography role="alert" sx={{ fontFamily: FONT, fontSize: 13, color: '#b3261e', fontWeight: 600 }}>
        Could not check existing leads. Please try this number again before checking in.
      </Typography>}

      {(isNew || picked) && (
        <Box>
          <Label htmlFor="fd-name">Name *</Label>
          <Box component="input" id="fd-name" value={newName} required
            autoCapitalize="words"
            aria-invalid={!!newName && !validVisitorName(newName)}
            aria-describedby={newName.includes('--') ? 'fd-name-help' : undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
            onBlur={() => setNewName((current) => picked ?
              joinBhaiSuffix(current) : formatFrontDeskName(current))}
            sx={{ width: '100%', boxSizing: 'border-box', height: 46, fontSize: 16, fontFamily: FONT, px: 1.5, border: '1px solid #d9d3c8', borderRadius: 2.5, outline: 'none' }} />
          {newName.includes('--') && <Typography id="fd-name-help" role="alert"
            sx={{ fontFamily: FONT, fontSize: 12.5, mt: 0.75, color: '#b3261e' }}>
            Enter a real name without --.
          </Typography>}
        </Box>
      )}

      <Box>
        <Label>Project *</Label>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {projects.map((p) => (
            <Pill key={p.id} selected={projectId === p.id} onClick={() => setProjectId(p.id)}>{p.name}</Pill>
          ))}
        </Box>
      </Box>

      <Box>
        <Label>Visit with *</Label>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {VISIT_WITH_OPTIONS.map((o) => (
            <Pill key={o.key} selected={visitWith === o.key} onClick={() => setVisitWith(o.key)}>{o.label}</Pill>
          ))}
          {visitWith && visitWith !== 'alone' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { sm: 1 } }}>
              <Box component="button" type="button" aria-label="Fewer people" onClick={() => setPeopleCount((n) => Math.max(2, n - 1))}
                sx={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid #d9d3c8', bgcolor: '#fff', fontSize: 18, cursor: 'pointer' }}>−</Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, minWidth: 70, textAlign: 'center' }}>{peopleCount} people</Typography>
              <Box component="button" type="button" aria-label="More people" onClick={() => setPeopleCount((n) => Math.min(20, n + 1))}
                sx={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid #d9d3c8', bgcolor: '#fff', fontSize: 18, cursor: 'pointer' }}>+</Box>
            </Box>
          )}
        </Box>
        {visitWith === 'other' && <TextField sx={{ mt: 1.25 }} fullWidth label="Who is with the visitor?" value={visitWithOther}
          onChange={(event) => setVisitWithOther(event.target.value)} />}
      </Box>

      <Box>
        <Label>Came via *</Label>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {CAME_VIA_OPTIONS.map((o) => (
            <Pill key={o.key} selected={cameVia === o.key} onClick={() => setCameVia(o.key)}>{o.label}</Pill>
          ))}
        </Box>
        {cameVia === 'broker' && (
          <Box sx={{ mt: 1.25 }}>
          <Label>Broker name *</Label>
          <Autocomplete
            options={brokerMatches}
            filterOptions={(options) => options}
            openOnFocus={false}
            open={brokerSearchOpen && brokerText.trim().length >= 3}
            onOpen={() => setBrokerSearchOpen(true)}
            onClose={() => setBrokerSearchOpen(false)}
            noOptionsText="No matching brokers"
            value={broker}
            onChange={(_, v) => {
              setBroker(typeof v === 'string' ? null : v);
              if (v && typeof v !== 'string') setUnlistedBrokerPhone('');
            }}
            inputValue={brokerText}
            onInputChange={(_, v, reason) => {
              setBrokerText(v);
              if (reason === 'input') setBroker(null);
            }}
            freeSolo
            getOptionLabel={(b) => (typeof b === 'string' ? b : `${b.brokerName}${b.agencyName ? ` · ${b.agencyName}` : ''}`)}
            renderInput={(params) => <TextField {...params} placeholder="Search or enter broker name"
              onBlur={() => { if (!broker) setBrokerText((current) => formatFrontDeskName(current)); }}
              inputProps={{ ...params.inputProps, 'aria-label': 'Broker name' }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, minHeight: 54 } }} />}
          />
          </Box>
        )}
        {cameVia === 'broker' && !broker && brokerText.trim().length > 1 && (
          <Box sx={{ mt: 1.25 }}>
            <Box>
              <Label htmlFor="fd-broker-phone">Broker mobile number *</Label>
              <Box component="input" id="fd-broker-phone" type="tel" inputMode="numeric" autoComplete="off"
                value={displayMobile(unlistedBrokerPhone)} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUnlistedBrokerPhone(mobileDigits(event.target.value))}
                placeholder="12345 67890" sx={{ width: '100%', boxSizing: 'border-box', height: 54, px: 2,
                  border: `1px solid ${LINE}`, borderRadius: 3, fontSize: 17, fontFamily: FONT }} />
              {!/^[6-9]\d{9}$/.test(unlistedBrokerPhone) &&
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: MUTED, mt: 0.5 }}>A valid mobile number links this broker to their profile form.</Typography>}
            </Box>
          </Box>
        )}
        {cameVia === 'c2c' && (
          <TextField sx={{ mt: 1.25 }} fullWidth label="C2C reference name" value={c2cName} onChange={(e) => setC2cName(e.target.value)} />
        )}
      </Box>

      {message && (
        <Typography role="status" sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: message.kind === 'ok' ? '#2f6b35' : '#b3261e' }}>{message.text}</Typography>
      )}
      <Button onClick={checkIn} disabled={!canCheckIn || saving} variant="contained" disableElevation
        sx={{ height: 56, borderRadius: 3.5, bgcolor: DARK, fontFamily: FONT, fontWeight: 700, fontSize: 17, textTransform: 'none', '&:hover': { bgcolor: '#33312d' } }}>
        {saving ? 'Checking in…' : 'Check in'}
      </Button>
    </Box>
  );

  const waitingVisits = rows.filter((r) => r.status === 'waiting');
  const waitingCount = waitingVisits.length;

  return (
    <Box sx={{ width: '100%', minHeight: standalone ? '100vh' : 'calc(100vh - 64px)', bgcolor: '#f7f5f1', fontFamily: FONT, color: '#1a1a1a' }}>
      {standalone && (
        <Box component="header" sx={{ bgcolor: DARK, color: '#fff', display: 'flex', alignItems: 'center', gap: 2, px: { xs: 2, md: 3.5 }, py: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>S</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700 }}>Front desk</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#d6d1c8' }}>
              {now.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
              {waitingCount ? ` · ${waitingCount} waiting` : ''}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#d6d1c8', display: { xs: 'none', sm: 'block' } }}>{currentUser?.email}</Typography>
          <Box component="button" type="button" onClick={() => signOut(auth)}
            sx={{ minHeight: 36, px: 1.5, borderRadius: 999, border: '1px solid #55524c', bgcolor: 'transparent', color: '#fff', fontFamily: FONT, fontSize: 13, cursor: 'pointer' }}>
            Log out
          </Box>
        </Box>
      )}
      {!standalone && (
        <Box sx={{ px: { xs: 2, md: 3.5 }, pt: 2 }}>
          <Typography component="h1" sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 700 }}>Front desk</Typography>
        </Box>
      )}

      <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, md: 3.5 } }}>
        {departureMessage && <Typography role="status"
          sx={{ fontFamily: FONT, fontSize: 13, color: '#2f6b35', mb: 1 }}>
          {departureMessage}
        </Typography>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Pill selected={visitMode === 'customer'} onClick={() => setVisitMode('customer')}>Customer visit</Pill>
          <Pill selected={visitMode === 'broker'} onClick={() => setVisitMode('broker')}>Broker visit</Pill>
        </Box>
        {visitMode === 'broker' ? <BrokerVisitPanel mode="desk" /> : <>
        {form}
        {waitingCount > 0 && (
          <Box component="section" aria-label="Waiting at reception" sx={{ bgcolor: '#fff8ec', border: '1px solid #f0c98f', borderRadius: 2, p: 2, mt: 2 }}>
            <Typography component="h2" sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, mb: 1 }}>
              Waiting at reception ({waitingCount})
            </Typography>
            {waitingVisits.map((visit) => (
              <Box key={visit.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, py: 1, borderTop: '1px solid #eadac2' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700 }}>{visit.name}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}>
                    {[receptionSummary(visit), `Arrived ${timeLabel(visit.checkedInAt)}`].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Box component="button" type="button" onClick={() => { setDepartureMessage(''); setLeaveFor(visit); }}
                  sx={{ flexShrink: 0, minHeight: 36, px: 1.5, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #d9d3c8', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  Visitor left
                </Box>
              </Box>
            ))}
          </Box>
        )}
        </>}
      </Box>
      <LeaveReceptionDialog visit={leaveFor} onClose={() => setLeaveFor(null)}
        onSave={async (visit: ReceptionVisit, reason: ReceptionDepartureReason, note: string) => {
          await leaveReceptionVisit(db, visit, { reason, note, byEmail: currentUser?.email || '' });
          setDepartureMessage(`${visit.name}'s departure was recorded.`);
        }} />
    </Box>
  );
}
